import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { Patient, Encounter, DiagnosticReport } from '@medplum/fhirtypes';
import { Button, Group, Text, Badge, Stack, Paper } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useMedplum } from '@medplum/react';
import { IconPlus } from '@tabler/icons-react';
import { ReportFileModal } from '../files-tab/ReportFileModal';
import { ReportCard } from '../files-tab/components/ReportCard';
import {
  REPORT_CATEGORY_OPTIONS,
  ReportCategoryValue,
} from '../files-tab/utils/report-files.consts';
import {
  getReportCategoryFromResource,
  fetchDiagnosticReportsForPatient,
  deleteDiagnosticReport,
} from '../files-tab/utils/report-files.utils';
import DeleteConfirmModal from '../images-tab/DeleteModal';

interface ExaminationFilesTabProps {
  patient: Patient;
  encounter: Encounter;
}

export const ExaminationFilesTab: FC<ExaminationFilesTabProps> = ({
  patient,
  encounter,
}) => {
  const { t } = useTranslation(['common', 'patient-pages']);
  const medplum = useMedplum();
  const [reports, setReports] = useState<DiagnosticReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportModalOpened, setReportModalOpened] = useState(false);
  const [reportToEdit, setReportToEdit] = useState<DiagnosticReport | null>(
    null
  );
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<DiagnosticReport | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  const loadDiagnosticReports = useCallback(async () => {
    if (!patient?.id) {
      return;
    }
    setLoadingReports(true);
    try {
      const data = await fetchDiagnosticReportsForPatient({
        medplum,
        patientId: patient.id,
        encounterId: encounter?.id,
      });
      setReports(data);
    } catch (error) {
      console.error('Failed to load diagnostic reports', error);
    } finally {
      setLoadingReports(false);
    }
  }, [encounter?.id, medplum, patient?.id]);

  useEffect(() => {
    loadDiagnosticReports();
  }, [loadDiagnosticReports]);

  const categorizedReports = useMemo(() => {
    const initialMap: Record<ReportCategoryValue, DiagnosticReport[]> = {
      genetic: [],
      investigation: [],
      biopsy: [],
      'prenatal-check': [],
      'pre-implantation-check': [],
    };
    return reports.reduce((acc, report) => {
      const category = getReportCategoryFromResource(report);
      acc[category] = [...acc[category], report];
      return acc;
    }, initialMap);
  }, [reports]);

  const handleOpenCreateReport = () => {
    setReportToEdit(null);
    setReportModalOpened(true);
  };

  const handleEditReport = (report: DiagnosticReport) => {
    setReportToEdit(report);
    setReportModalOpened(true);
  };

  const handleDeleteReport = (report: DiagnosticReport) => {
    setReportToDelete(report);
    setDeleteModalOpened(true);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) {
      return;
    }

    setDeleting(true);
    try {
      await deleteDiagnosticReport(medplum, reportToDelete);
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Failed to delete diagnostic report', error);
    } finally {
      setDeleting(false);
    }

    try {
      await loadDiagnosticReports();
    } catch (error) {
      console.error('Failed to reload diagnostic reports after delete', error);
    }
  };

  const handleCloseDeleteModal = () => {
    if (!deleting) {
      setDeleteModalOpened(false);
      setReportToDelete(null);
    }
  };

  const handleReportModalClose = () => {
    setReportModalOpened(false);
    setReportToEdit(null);
  };

  const handleReportModalSuccess = () => {
    loadDiagnosticReports();
  };

  const renderCategorySection = (
    categoryOption: (typeof REPORT_CATEGORY_OPTIONS)[number]
  ) => {
    const categoryReports = categorizedReports[categoryOption.value] || [];
    return (
      <Stack key={categoryOption.value} gap="sm">
        <Group gap="xs">
          <Text fw={500}>
            {t(`reportCards.categoryOptions.${categoryOption.label}`, {
              ns: 'patient-pages',
              defaultValue: categoryOption.label,
            })}
          </Text>
          <Badge size="xs" variant="light" mt={3}>
            {categoryReports.length}
          </Badge>
        </Group>
        {categoryReports.length ? (
          <Stack gap="sm">
            {categoryReports.map((report) => (
              <ReportCard
                key={report.id}
                report={report}
                patient={patient}
                onEdit={handleEditReport}
                onDelete={handleDeleteReport}
              />
            ))}
          </Stack>
        ) : (
          <Paper p="xl" withBorder radius="md" bg="gray.0">
            <Text size="sm" c="dimmed" ta="center">
              {t('reportCards.empty', {
                ns: 'patient-pages',
                defaultValue: 'Δεν υπάρχουν αναφορές για αυτή την κατηγορία.',
              })}
            </Text>
          </Paper>
        )}
      </Stack>
    );
  };

  return (
    <>
      <Button
        fullWidth
        leftSection={<IconPlus size={16} />}
        variant="light"
        onClick={handleOpenCreateReport}
      >
        {t('reportCards.add', {
          ns: 'patient-pages',
          defaultValue: 'Προσθήκη αναφοράς',
        })}
      </Button>
      <Stack gap="md">
        {loadingReports ? (
          <Paper p="xl" withBorder radius="md">
            <Text size="sm" c="dimmed">
              {t('reportCards.loading', {
                ns: 'patient-pages',
                defaultValue: 'Φόρτωση…',
              })}
            </Text>
          </Paper>
        ) : (
          REPORT_CATEGORY_OPTIONS.map(renderCategorySection)
        )}
      </Stack>

      <ReportFileModal
        opened={reportModalOpened}
        onClose={handleReportModalClose}
        patient={patient}
        encounter={encounter}
        report={reportToEdit}
        onSuccess={handleReportModalSuccess}
      />

      <DeleteConfirmModal
        opened={deleteModalOpened}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        message={t('reportCards.deleteConfirm', {
          ns: 'patient-pages',
          defaultValue:
            'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την αναφορά;',
        })}
      />
    </>
  );
};
